import { useState } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { roleLabels } from '@/types/database';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Bell,
  Menu,
  ChevronDown,
  Settings,
  LogOut,
  HelpCircle,
  AlertCircle,
  Clock,
  FileText,
  MapPin,
  CheckCircle,
  Stethoscope,
  CreditCard,
  BarChart3,
  Phone,
  X,
  MessageCircle,
} from 'lucide-react';
import { MobileBottomNav } from './MobileBottomNav';
import { NotificacionesPopover } from './NotificacionesPopover';
import { FloatingSubscriptionBanner } from '@/components/professional/FloatingSubscriptionBanner';
import logoUrgSalud from '@/assets/logo-urgsalud-transparent.png';

const professionalNavItems = [
  { label: 'Inicio', href: '/profesional', icon: LayoutDashboard },
  { label: 'Mi Perfil', href: '/perfil', icon: Stethoscope },
  { label: 'Solicitudes Cercanas', href: '/solicitudes', icon: MapPin },
  { label: 'Calendario', href: '/disponibilidad', icon: Clock },
  { label: 'Mis Pacientes', href: '/mis-atenciones', icon: CheckCircle },
  { label: 'Comunidad', href: '/comunidad', icon: MessageCircle },
  { label: 'Red de Profesionales', href: '/red-profesionales', icon: Stethoscope },
  { label: 'Estadísticas', href: '/estadisticas', icon: BarChart3 },
  { label: 'Preguntas Frecuentes', href: '/faq-profesional', icon: HelpCircle },
];

// Items for mobile bottom nav (most important ones)
const mobileNavItems = [
  { label: 'Inicio', href: '/profesional', icon: LayoutDashboard },
  { label: 'Solicitudes', href: '/solicitudes', icon: MapPin },
  { label: 'Atenciones', href: '/mis-atenciones', icon: CheckCircle },
  { label: 'Perfil', href: '/perfil', icon: Stethoscope },
  { label: 'Stats', href: '/estadisticas', icon: BarChart3 },
];

export default function ProfessionalLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { profile, role, signOut } = useAuth();
  const location = useLocation();

  const currentPageTitle = professionalNavItems.find(item => item.href === location.pathname)?.label || 'Panel Profesional';

  const getRoleBadgeClass = () => {
    switch (role) {
      case 'medico': return 'bg-role-medico/15 text-role-medico';
      case 'enfermera': return 'bg-role-enfermera/15 text-role-enfermera';
      default: return 'bg-secondary/15 text-secondary';
    }
  };

  return (
    <div className="min-h-dvh bg-background">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Professional Theme (Secondary/Blue) */}
      <aside
        className={cn(
          'fixed right-0 top-0 z-50 h-full w-64 bg-gradient-to-b from-secondary to-secondary/90 transform transition-transform duration-300 lg:translate-x-0 shadow-xl',
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo & Close button (mobile) */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <Link to="/profesional"><img src={logoUrgSalud} alt="UrgSalud Chile" className="h-9 w-auto brightness-0 invert" width={36} height={36} /></Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white/70 hover:text-white hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Role indicator */}
          <div className="px-5 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-white/10">
                <Stethoscope className="w-4 h-4 text-white" />
              </div>
              <div>
                <span className="text-xs text-white/60 uppercase tracking-wide font-medium">Panel de</span>
                <p className="text-sm font-semibold text-white">Profesional</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {professionalNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <li key={item.href}>
                    <NavLink
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                        isActive
                          ? 'bg-white text-secondary shadow-md'
                          : 'text-white/80 hover:text-white hover:bg-white/10'
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Status indicator */}
          <div className="px-4 py-3 mx-3 mb-3 rounded-lg bg-success/20 border border-success/30">
            <div className="flex items-center gap-2 text-xs text-white/90">
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span className="font-medium">Estado: Disponible</span>
            </div>
          </div>

          {/* User section at bottom */}
          <div className="border-t border-white/10 p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 ring-2 ring-white/20">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-white/20 text-white text-sm font-medium">
                  {profile?.nombre?.[0]}{profile?.apellido?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {profile?.nombre} {profile?.apellido}
                </p>
                <p className="text-xs text-white/60 truncate">
                  {role ? roleLabels[role] : 'Profesional'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pr-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-5 h-5" />
            </Button>

            {/* Page title */}
            <div className="hidden lg:flex items-center gap-3">
              <h1 className="text-lg font-semibold text-foreground">{currentPageTitle}</h1>
              <Badge variant="outline" className="text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-success mr-1.5" />
                Activo
              </Badge>
            </div>

            {/* Mobile logo */}
            <Link to="/profesional" className="lg:hidden">
              <img src={logoUrgSalud} alt="UrgSalud" className="h-8 w-auto" width={32} height={32} />
            </Link>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <NotificacionesPopover />

              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url || undefined} />
                      <AvatarFallback className="bg-secondary/10 text-secondary text-xs">
                        {profile?.nombre?.[0]}{profile?.apellido?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium">{profile?.nombre}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col gap-1">
                      <span>{profile?.nombre} {profile?.apellido}</span>
                      <Badge className={cn('w-fit text-xs', getRoleBadgeClass())}>
                        {role ? roleLabels[role] : 'Profesional'}
                      </Badge>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <NavLink to="/perfil" className="flex items-center gap-2 cursor-pointer">
                      <Stethoscope className="w-4 h-4" />
                      Mi Perfil Profesional
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <NavLink to="/ayuda" className="flex items-center gap-2 cursor-pointer">
                      <HelpCircle className="w-4 h-4" />
                      Ayuda
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={signOut}
                    className="text-destructive focus:text-destructive cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content - add bottom padding on mobile for nav */}
        <main className="p-4 lg:p-6 pb-24 lg:pb-6">
          {children}
        </main>
      </div>


      {/* Mobile bottom navigation */}
      <MobileBottomNav items={mobileNavItems} activeColor="text-secondary" />
    </div>
  );
}
